import { Check, Lock, Mail, Phone, User, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { money } from '../LiveBasket';

function validate(customer) {
  const name = customer.name.trim().length >= 2;
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email);
  const phone = customer.phone.replace(/\D/g, '').length >= 9;
  return { name, email, phone, all: name && email && phone };
}

function SummaryBlock({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] p-4">
      <p className="text-[9px] uppercase tracking-widest text-slate-500 dark:text-slate-500 font-bold mb-2">{title}</p>
      {children}
    </div>
  );
}

function Line({ label, value, accent }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-xs text-slate-400 dark:text-slate-400 flex-1 min-w-0 truncate">{label}</span>
      <span className={`text-xs font-mono font-semibold flex-shrink-0 ${accent ? 'text-[#00E5FF]' : 'text-[#0A0A0A] dark:text-white'}`}>{value}</span>
    </div>
  );
}

export default function StepReview({ state, setupTotal, monthlyTotal, customer, setCustomer, agreed, setAgreed }) {
  const v = validate(customer);

  const onChange = (field) => (e) => setCustomer({ ...customer, [field]: e.target.value });

  return (
    <div data-testid="step-review" className="animate-[fadeIn_0.4s_ease]">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2 font-bold">Step 5 · Review &amp; Send</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white leading-tight">Ready to lock it in?</h2>
        <p className="text-sm text-slate-400 dark:text-slate-400 mt-2 max-w-md">Last check before I build your quote. Pop your details below and I'll email it back within the hour.</p>
      </div>

      {/* Selections summary */}
      <div className="space-y-3 mb-6">
        <SummaryBlock title="Foundation">
          {state.tier ? (
            <Line label={`${state.tier.name} · ${state.tier.tagline}`} value={money(state.tier.price)} accent />
          ) : <p className="text-xs text-rose-300">No foundation selected — go back to Step 1</p>}
        </SummaryBlock>

        <SummaryBlock title={`Bolt-ons · ${state.boltOns.length}`}>
          {state.boltOns.length ? state.boltOns.map(b => {
            const boost = state.boosts?.[b.id];
            return (
              <div key={b.id}>
                <Line label={b.name} value={`${money(b.price)}${b.price_suffix || ''}`} />
                {boost && (
                  <Line
                    label={<span className="pl-3 text-slate-500 dark:text-slate-500">↳ {boost.name} <span className="text-[10px] opacity-70">({boost.credits})</span></span>}
                    value={`+${money(boost.price_monthly)}/mo`}
                    accent
                  />
                )}
              </div>
            );
          }) : <p className="text-xs text-slate-500 dark:text-slate-500 italic">None selected</p>}
        </SummaryBlock>

        {state.brief?.trim() && (
          <SummaryBlock title="Your brief">
            <p className="text-xs text-slate-400 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">{state.brief}</p>
          </SummaryBlock>
        )}

        <SummaryBlock title="Ecosystem">
          {state.carePlan
            ? <Line label={state.carePlan.name} value={`${money(state.carePlan.price_monthly)}/mo`} accent />
            : <Line label="Care plan" value="Skipped" />}
          {state.hosting && !state.skipHosting
            ? <Line label={`${state.hosting.name} hosting · ${state.hosting.ram}`} value={state.hosting.monthly_surcharge === 0 ? 'Included' : `+${money(state.hosting.monthly_surcharge)}/mo`} accent />
            : <Line label="Hosting" value="Skipped" />}
        </SummaryBlock>
      </div>

      {/* Totals */}
      <div className="mb-6 rounded-2xl overflow-hidden border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/10 via-transparent to-violet-500/10">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-400 font-bold">One-time setup</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-0.5">Paid before build begins</p>
          </div>
          <span className="font-['Outfit'] text-3xl font-black text-[#0A0A0A] dark:text-white">{money(setupTotal)}</span>
        </div>
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-400 font-bold">Monthly recurring</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-0.5">Care plan + hosting</p>
          </div>
          <span className="font-['Outfit'] text-3xl font-black text-[#0A0A0A] dark:text-white">{money(monthlyTotal)}<span className="text-sm text-slate-500 dark:text-slate-500 font-normal">/mo</span></span>
        </div>
      </div>

      {/* Lead capture */}
      <div className="space-y-3 mb-6">
        <div className="relative">
          <User className="w-4 h-4 text-slate-500 dark:text-slate-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            data-testid="review-name"
            value={customer.name}
            onChange={onChange('name')}
            placeholder="Your full name"
            className="w-full bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:text-slate-600 focus:outline-none focus:border-[#00E5FF]/60 focus:bg-[#00E5FF]/[0.03] transition-all"
          />
          {customer.name && (v.name
            ? <Check className="w-4 h-4 text-emerald-400 absolute right-4 top-1/2 -translate-y-1/2" />
            : <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-rose-400">min 2 chars</span>)}
        </div>

        <div className="relative">
          <Mail className="w-4 h-4 text-slate-500 dark:text-slate-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="email"
            data-testid="review-email"
            value={customer.email}
            onChange={onChange('email')}
            placeholder="you@example.com"
            className="w-full bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:text-slate-600 focus:outline-none focus:border-[#00E5FF]/60 focus:bg-[#00E5FF]/[0.03] transition-all"
          />
          {customer.email && (v.email
            ? <Check className="w-4 h-4 text-emerald-400 absolute right-4 top-1/2 -translate-y-1/2" />
            : <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-rose-400">invalid</span>)}
        </div>

        <div className="relative">
          <Phone className="w-4 h-4 text-slate-500 dark:text-slate-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="tel"
            data-testid="review-phone"
            value={customer.phone}
            onChange={onChange('phone')}
            placeholder="+44 7XXX XXXXXX"
            className="w-full bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:text-slate-600 focus:outline-none focus:border-[#00E5FF]/60 focus:bg-[#00E5FF]/[0.03] transition-all"
          />
          {customer.phone && (v.phone
            ? <Check className="w-4 h-4 text-emerald-400 absolute right-4 top-1/2 -translate-y-1/2" />
            : <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-rose-400">9+ digits</span>)}
        </div>
      </div>

      {/* Consent */}
      <label className="flex items-start gap-3 cursor-pointer mb-5 group">
        <input
          type="checkbox"
          data-testid="review-terms"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-white/20 bg-slate-100 dark:bg-white/5 accent-[#00E5FF] cursor-pointer"
        />
        <span className="text-xs text-slate-400 dark:text-slate-400 leading-relaxed">
          I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#00E5FF] hover:text-[#0A0A0A] dark:hover:text-white underline underline-offset-2">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#00E5FF] hover:text-[#0A0A0A] dark:hover:text-white underline underline-offset-2">Privacy Policy</Link>. I consent to being contacted about this quote.
        </span>
      </label>

      {/* Trust signals */}
      <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-100 dark:border-white/5">
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-500">
          <Lock className="w-3 h-3 text-emerald-400" /> SSL Secured
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-500">
          <ShieldCheck className="w-3 h-3 text-emerald-400" /> GDPR Compliant
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-500">
          <Zap className="w-3 h-3 text-[#00E5FF]" /> No spam, ever
        </span>
      </div>
    </div>
  );
}

export { validate };
